/**
 * Cache wrappers for the Robots router. Two distinct caches:
 *
 * - `CachedRobotsList` — TtlCache around `API.Data.Robots.get.all()` (15-min). Single entry,
 *   in-flight coalescing. The Scheduling board reads it via `refresh()`, not `get()` — on mount
 *   and on its refresh gesture — since robot positions move continuously. The Robots admin page
 *   streams positions and never reads this cache.
 * - `CachedRobotsDetail` — RangeCache pair (raw + timeline) around
 *   `API.Data.Robots.byIdWithDetail(...)`. Raw is 4-hour chunked with bounded concurrency for
 *   progress reporting; timeline is fetched once across the whole requested range regardless of
 *   chunk count (see `getInRange` for why). The two are combined into a single API call when the
 *   whole range fits in one chunk and their gap sets match.
 */

import { Robot, type RobotRaw, type RobotTimeline } from "@domain/CRM/robot";
import { RangeCache, type Range } from "../../../cache/rangeCache";
import { TtlCache } from "../../../cache/ttlCache";
import { API } from "../../../api";
import type { RobotDetailOpts as RawRobotDetailOpts } from "../../../api/routers/data/tables/robots";

const LIST_TTL_MS = 15 * 60 * 1000;
const LIST_KEY = "all" as const;
const CHUNK_MS = 4 * 60 * 60 * 1000;
const CONCURRENCY = 6;

export interface RobotDetailRangeOpts extends RawRobotDetailOpts {
	/** Called as each chunk completes; `done` and `total` count chunks (one tick per chunk). */
	onProgress?: (done: number, total: number) => void;
	/** When aborted, the worker pool drains in-flight chunks and rejects with `AbortError`. */
	signal?: AbortSignal;
}

function computeChunks(startMs: number, endMs: number): Array<[number, number]> {
	const chunks: Array<[number, number]> = [];
	for (let s = startMs; s < endMs; s += CHUNK_MS) {
		chunks.push([s, Math.min(s + CHUNK_MS, endMs)]);
	}
	return chunks;
}

function gapsEqual(a: Range[], b: Range[]): boolean {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i][0] !== b[i][0] || a[i][1] !== b[i][1]) return false;
	}
	return true;
}

class CachedRobotsList {
	private cache = new TtlCache<typeof LIST_KEY, Robot[]>(LIST_TTL_MS, () => API.Data.Robots.get.all());

	/** Synchronous TTL cache hit, or null if missing/expired. */
	public peek(): Robot[] | null {
		return this.cache.peek(LIST_KEY);
	}

	public get(): Promise<Robot[]> {
		return this.cache.get(LIST_KEY);
	}

	/**
	 * Bypass BOTH caches — the server's (`?fresh=1`) and this TTL entry — and overwrite the entry
	 * with the live result. Used by the Scheduling board on mount and on its refresh-in-place
	 * gesture: robot positions move continuously, so neither cache is ever invalidated by our own
	 * writes. Unlike `get()`, concurrent callers each fetch — this is a deliberate bypass, not a
	 * join. A failed fetch leaves the previous entry in place.
	 */
	public async refresh(): Promise<Robot[]> {
		const robots = await API.Data.Robots.get.all(true);
		this.cache.set(LIST_KEY, robots);
		return robots;
	}

	public clear(): void {
		this.cache.clear();
	}
}

class CachedRobotsDetail {
	private rawCache = new RangeCache<RobotRaw>({ timestampOf: (r) => r.timestamp });
	private timelineCache = new RangeCache<RobotTimeline>({ timestampOf: (t) => t.start });
	/** Per-robot snapshot of the most recent base fields (lastUpdated, name, …). Re-stamped on every fetch. */
	private baseRobotCache = new Map<string, Robot>();

	/**
	 * Range-aware fetch of a robot's raw/timeline data. Reuses cached items + fetches only the
	 * gaps. Raw is range-indexed server-side, so it's split into 4-hour chunks executed with
	 * bounded concurrency for progress reporting. Timeline is NOT range-indexed — the backend
	 * (`DynamoDB.RobotTimeline.get()`) falls back to an unindexed full-table scan whenever it's
	 * fetched without a `type` partition key, which this caller never has. That scan costs
	 * roughly the same regardless of how narrow the requested window is, so splitting it into the
	 * same per-chunk pieces as raw would multiply an already-expensive call by the chunk count for
	 * zero benefit (this is what produced the "N+1 API Call" Sentry alerts on this endpoint —
	 * several concurrent 4-hour chunk requests each paying the full scan cost). Timeline gaps are
	 * therefore resolved once across the FULL requested range, outside the per-chunk loop. For the
	 * common single-chunk case (the whole window fits in one 4-hour chunk) the two are still
	 * combined into one API call when their gap sets match, exactly as before.
	 */
	public async getInRange(id: string, start: Date, end: Date, opts: RobotDetailRangeOpts): Promise<Robot> {
		const startMs = start.getTime();
		const endMs = end.getTime();

		// Cold path: caller asked for neither raw nor timeline. Defer to the direct API.
		if (!opts.raw && !opts.timeline) {
			const base = await API.Data.Robots.byIdWithDetail(id, start, end, opts);
			this.baseRobotCache.set(id, base);
			return base;
		}

		const wantRaw = !!opts.raw;
		const wantTimeline = !!opts.timeline;
		const chunks = computeChunks(startMs, endMs);
		const total = chunks.length;
		let done = 0;
		let cursor = 0;
		const workerCount = Math.min(CONCURRENCY, total);

		const timelineGaps = wantTimeline ? this.timelineCache.gaps(id, [startMs, endMs]) : [];
		// Only the single-chunk case can combine raw + timeline into one call — beyond that, raw's
		// per-chunk gaps and timeline's single full-range gap can never line up.
		const combineSingleChunk = total <= 1 && wantRaw && wantTimeline;

		const fetchChunk = async (chunk: Range): Promise<void> => {
			const rawGaps = wantRaw ? this.rawCache.gaps(id, chunk) : [];

			if (combineSingleChunk && gapsEqual(rawGaps, timelineGaps)) {
				for (const gap of rawGaps) {
					const r = await API.Data.Robots.byIdWithDetail(id, new Date(gap[0]), new Date(gap[1]), {
						raw: true,
						timeline: true,
					});
					this.baseRobotCache.set(id, r);
					this.rawCache.ingest(id, gap, r.raw ?? []);
					this.timelineCache.ingest(id, gap, r.timeline ?? []);
				}
				return;
			}

			for (const gap of rawGaps) {
				const r = await API.Data.Robots.byIdWithDetail(id, new Date(gap[0]), new Date(gap[1]), { raw: true });
				this.baseRobotCache.set(id, r);
				this.rawCache.ingest(id, gap, r.raw ?? []);
			}
		};

		const fetchTimeline = async (): Promise<void> => {
			if (combineSingleChunk) return; // handled inline alongside raw above
			for (const gap of timelineGaps) {
				if (opts.signal?.aborted) {
					throw new DOMException("Aborted", "AbortError");
				}
				const r = await API.Data.Robots.byIdWithDetail(id, new Date(gap[0]), new Date(gap[1]), { timeline: true });
				this.baseRobotCache.set(id, r);
				this.timelineCache.ingest(id, gap, r.timeline ?? []);
			}
		};

		await Promise.all([
			Promise.all(
				Array.from({ length: workerCount }, async () => {
					while (cursor < total) {
						if (opts.signal?.aborted) {
							throw new DOMException("Aborted", "AbortError");
						}
						const i = cursor++;
						await fetchChunk(chunks[i]);
						done++;
						opts.onProgress?.(done, total);
					}
				}),
			),
			fetchTimeline(),
		]);

		let base = this.baseRobotCache.get(id);
		if (!base) {
			// Defensive: every chunk hit cache and we've never fetched this robot's base fields. Force one.
			base = await API.Data.Robots.byIdWithDetail(id, start, end, opts);
			this.baseRobotCache.set(id, base);
			return base;
		}

		const out = Object.assign(new Robot(), base);
		if (wantRaw) {
			out.raw = chunks.flatMap((chunk) => this.rawCache.slice(id, chunk));
		} else {
			delete out.raw;
		}
		if (wantTimeline) {
			out.timeline = chunks.flatMap((chunk) => this.timelineCache.slice(id, chunk));
		} else {
			delete out.timeline;
		}
		return out;
	}

	public clear(): void {
		this.rawCache.clear();
		this.timelineCache.clear();
		this.baseRobotCache.clear();
	}
}

export class CachedRobotsAPI {
	public list = new CachedRobotsList();
	public detail = new CachedRobotsDetail();
}
